import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, TextField, Button, Paper, Alert, Grid, Divider, Tabs, Tab,
  Card, CardContent, IconButton, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Select, MenuItem, FormControl, InputLabel, CircularProgress, InputAdornment,
  Autocomplete // 👈 AGREGA ESTA PALABRA AQUÍ
} from '@mui/material';

// Iconos
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LockIcon from '@mui/icons-material/Lock';
import ImageIcon from '@mui/icons-material/Image';
import DeleteIcon from '@mui/icons-material/Delete';
import DrawIcon from '@mui/icons-material/Draw';
import PeopleIcon from '@mui/icons-material/People';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BusinessIcon from '@mui/icons-material/Business';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'https://luiatro-health-cloud-production.up.railway.app';

// --- INTERFACES ---
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface UserFormData {
  id?: string;
  name: string;
  lastname: string;
  username: string;
  cedula: string;
  email: string;
  password: string;
  role: string;
  prefix?: string;
  specialty?: string;
  mpps_number?: string;
  professional_college?: string;
  college_number?: string;
}

// --- HELPER COMPONENT: TAB PANEL ---
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// --- SUB-COMPONENTE: LOCATION MANAGER (Gestor de Recursos) ---
const LocationManager: React.FC = () => {
  const [locations, setLocations] = useState<any[]>([]);
  const [availabilities, setAvailabilities] = useState<any[]>([]);
  const [doctors, setDoctors] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Formulario Ubicación
  const [locForm, setLocForm] = useState({ name: '', type: 'CONSULTORIO', description: '' });
  
  // Formulario Disponibilidad
  const [availForm, setAvailForm] = useState({
    doctorId: '',
    locationId: '',
    dayOfWeek: 1,
    startTime: '08:00',
    endTime: '12:00'
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const [resLoc, resAvail, resUsers] = await Promise.all([
        fetch(`${API_URL}/api/locations`, { headers }),
        fetch(`${API_URL}/api/availability`, { headers }),
        fetch(`${API_URL}/api/users`, { headers })
      ]);
      if(resLoc.ok) setLocations(await resLoc.json());
      if(resAvail.ok) setAvailabilities(await resAvail.json());
      if(resUsers.ok) {
        const users = await resUsers.json();
        setDoctors(users.filter((u: any) => u.role === 'medico'));
      }
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreateLocation = async () => {
    if(!locForm.name) return alert("Nombre requerido");
    
    const token = localStorage.getItem('token');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    await fetch(`${API_URL}/api/locations`, {
      method: 'POST',
      headers,
      body: JSON.stringify(locForm)
    });
    setLocForm({ name: '', type: 'CONSULTORIO', description: '' });
    fetchData();
  };

  const handleCreateAvailability = async () => {
    if(!availForm.doctorId || !availForm.locationId) return alert("Complete los campos");
    
    const token = localStorage.getItem('token');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    await fetch(`${API_URL}/api/availability`, {
      method: 'POST',
      headers,
      body: JSON.stringify(availForm)
    });
    fetchData();
  };

  const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

  if (loading) return <CircularProgress />;

  return (
    <Grid container spacing={4}>
      {/* GESTIÓN DE ESPACIOS FÍSICOS */}
      <Grid item xs={12} md={5}>
        <Paper elevation={3} sx={{ p: 2, height: '100%', borderTop: '4px solid #1976d2' }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon color="primary" /> Espacios Físicos
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            <TextField 
              label="Nombre (Ej: Consultorio 1)" 
              size="small" 
              fullWidth 
              value={locForm.name} 
              onChange={e => setLocForm({...locForm, name: e.target.value})} 
            />
            <TextField 
              select 
              label="Tipo" 
              size="small" 
              sx={{ width: 150 }}
              value={locForm.type}
              onChange={e => setLocForm({...locForm, type: e.target.value})}
            >
              <MenuItem value="CONSULTORIO">Consultorio</MenuItem>
              <MenuItem value="QUIROFANO">Quirófano</MenuItem>
            </TextField>
            <Button variant="contained" onClick={handleCreateLocation}><AddIcon /></Button>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead><TableRow><TableCell>Nombre</TableCell><TableCell>Tipo</TableCell></TableRow></TableHead>
              <TableBody>
                {locations.map(loc => (
                  <TableRow key={loc.id}>
                    <TableCell>{loc.name}</TableCell>
                    <TableCell>
                      <Chip label={loc.type} size="small" color={loc.type === 'QUIROFANO' ? 'error' : 'primary'} variant="outlined" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>

      {/* ASIGNACIÓN DE HORARIOS */}
      <Grid item xs={12} md={7}>
        <Paper elevation={3} sx={{ p: 2, height: '100%', borderTop: '4px solid #ed6c02' }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTimeIcon color="warning" /> Disponibilidad Médica
          </Typography>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <TextField 
                select fullWidth label="Médico" size="small"
                value={availForm.doctorId}
                onChange={e => setAvailForm({...availForm, doctorId: e.target.value})}
              >
                {doctors.map(d => <MenuItem key={d.id} value={d.id}>{d.name} {d.lastname}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField 
                select fullWidth label="Lugar" size="small"
                value={availForm.locationId}
                onChange={e => setAvailForm({...availForm, locationId: e.target.value})}
              >
                {locations.map(l => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField 
                select fullWidth label="Día" size="small"
                value={availForm.dayOfWeek}
                onChange={e => setAvailForm({...availForm, dayOfWeek: parseInt(e.target.value)})}
              >
                {days.map((d, i) => i > 0 && <MenuItem key={i} value={i}>{d}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField 
                type="time" label="Inicio" fullWidth size="small"
                value={availForm.startTime} onChange={e => setAvailForm({...availForm, startTime: e.target.value})}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField 
                type="time" label="Fin" fullWidth size="small"
                value={availForm.endTime} onChange={e => setAvailForm({...availForm, endTime: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <Button fullWidth variant="contained" color="warning" onClick={handleCreateAvailability}>Asignar Horario</Button>
            </Grid>
          </Grid>

          <TableContainer sx={{ maxHeight: 300 }}>
            <Table size="small">
              <TableHead><TableRow><TableCell>Médico</TableCell><TableCell>Lugar</TableCell><TableCell>Horario</TableCell></TableRow></TableHead>
              <TableBody>
                {availabilities.map(av => (
                  <TableRow key={av.id}>
                    <TableCell>{av.doctor?.name} {av.doctor?.lastname}</TableCell>
                    <TableCell>{av.location?.name}</TableCell>
                    <TableCell>
                      {days[av.dayOfWeek]} ({av.startTime} - {av.endTime})
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>
    </Grid>
  );
};

const ALL_SPECIALTIES = [
    "Anestesiología", "Anatomía Patológica", "Cardiología", "Cirugía General", 
    "Dermatología", "Endocrinología", "Gastroenterología", "Geriatría", 
    "Ginecología y Obstetricia", "Hematología", "Infectología", "Medicina Interna", 
    "Nefrología", "Neumonología", "Neurología", "Oftalmología", "Oncología Médica", 
    "Otorrinolaringología", "Pediatría y Puericultura", "Psiquiatría", 
    "Radiología e Imagenología", "Reumatología", "Traumatología y Ortopedia", "Urología"
].sort();

// --- COMPONENTE PRINCIPAL ---
const ConfigurationPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'administrador';
  
  const [tabValue, setTabValue] = useState(0);
  
  // Estados para configuración GLOBAL (solo admin)
  const [config, setConfig] = useState({ 
    doctorName: '', 
    companyInfo: '', 
    footerAddress: '', 
    logoCartaUrl: '', 
    logoRecipeUrl: '', 
    logoUrl: '', 
    font: 'Arial', 
    fontSize: 8,
    favoriteSpecialties: '' as string | string[]// 👈 AÑADE ESTO
  });
  
  // Estado temporal para el Autocomplete
  const [selectedFavs, setSelectedFavs] = useState<string[]>([]);
  
  // Estados para configuración PERSONAL (firma digital)
  const [userConfig, setUserConfig] = useState({
    cedula: '',
    signature_url: '',
    prefix: '',
    specialty: '',
    mpps_number: '',
    professional_college: '',
    college_number: ''
  });
  
  // Estados para gestión de usuarios (solo admin)
  const [users, setUsers] = useState<any[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserFormData | null>(null);
  const [userFormData, setUserFormData] = useState<UserFormData>({
    name: '',
    lastname: '',
    username: '',
    cedula: '',
    email: '',
    password: '',
    role: 'medico',
    prefix: '',
    specialty: '',
    mpps_number: '',
    professional_college: '',
    college_number: ''
  });
  
  // Estados para archivos de logos (admin)
  const [logoCartaFile, setLogoCartaFile] = useState<File | null>(null);
  const [logoRecipeFile, setLogoRecipeFile] = useState<File | null>(null);
  
  // Estados para firma digital (usuario)
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  
  // Estados para previews
  const [previewCarta, setPreviewCarta] = useState<string>('');
  const [previewRecipe, setPreviewRecipe] = useState<string>('');
  const [previewSignature, setPreviewSignature] = useState<string>('');
  
  // Estados para contraseña
  const [passData, setPassData] = useState({ 
    oldPassword: '', 
    newPassword: '', 
    confirmPassword: '' 
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState<{ type: 'success'|'error', text: string } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const logoCartaInputRef = useRef<HTMLInputElement>(null);
  const logoRecipeInputRef = useRef<HTMLInputElement>(null);
  const signatureInputRef = useRef<HTMLInputElement>(null);

  // Función para procesar URLs de imágenes
  const processImageUrl = (url: string | undefined) => {
    if (!url) return '';
    if (url.startsWith('data:') || url.startsWith('http')) return url;
    
    const cleanPath = url.replace(/\\/g, '/');
    const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
    const finalPath = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
    
    return `${baseUrl}${finalPath}?t=${new Date().getTime()}`;
  };

  // ===== CARGAR USUARIOS (ADMIN) =====
  const fetchUsers = async () => {
    if (!isAdmin) return;
    setIsLoadingUsers(true);
    try {
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API_URL}/api/users`, { headers });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (err) {
      console.error('Error cargando usuarios:', err);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Cargar configuración inicial
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;

        // Cargar config global (admin)
        if (isAdmin) {
          const res = await fetch(`${API_URL}/api/config`, { headers });
          if (res.ok) {
            const data = await res.json();
            setConfig(data);
  
		// 👈 AÑADE ESTO:
		if (data.favoriteSpecialties) {
			const parsed = typeof data.favoriteSpecialties === 'string' 
				? JSON.parse(data.favoriteSpecialties) 
				: data.favoriteSpecialties;
			setSelectedFavs(parsed);
		}
  
             // Procesar URLs de logos
            if (data.logoCartaUrl) {
                setPreviewCarta(processImageUrl(data.logoCartaUrl));
            }
            if (data.logoRecipeUrl) {
                setPreviewRecipe(processImageUrl(data.logoRecipeUrl));
            }
          }
          
          // Cargar usuarios
          fetchUsers();
        }
        
        // Cargar config personal (todos los usuarios)
        if (user?.id) {
          const userRes = await fetch(`${API_URL}/api/users/${user.id}`, { headers });
          if (userRes.ok) {
            const userData = await userRes.json();
            setUserConfig({
              cedula: userData.cedula || '',
              signature_url: userData.signatureUrl || userData.signature_url || '', 
              prefix: userData.prefix || '',
              specialty: userData.specialty || '',
              mpps_number: userData.mppsNumber || userData.mpps_number || '', 
              professional_college: userData.professionalCollege || userData.professional_college || '', 
              college_number: userData.collegeNumber || userData.college_number || '' 
            });
            
            if (userData.signatureUrl || userData.signature_url) {
              setPreviewSignature(processImageUrl(userData.signatureUrl || userData.signature_url));
            }
          }
        }
      } catch (error) {
        console.error("Error cargando config:", error);
      }
    };
    
    fetchConfig();
  }, [isAdmin, user?.id]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // ===== GESTIÓN DE USUARIOS (ADMIN) =====
  const handleOpenUserDialog = (userToEdit?: any) => {
    if (userToEdit) {
      setEditingUser(userToEdit);
      setUserFormData({
        id: userToEdit.id,
        name: userToEdit.name || '',
        lastname: userToEdit.lastname || '',
        username: userToEdit.username || '',
        cedula: userToEdit.cedula || '',
        email: userToEdit.email || '',
        password: '',
        role: userToEdit.role || 'medico',
        prefix: userToEdit.prefix || '',
        specialty: userToEdit.specialty || '',
        mpps_number: userToEdit.mppsNumber || userToEdit.mpps_number || '',
        professional_college: userToEdit.professionalCollege || userToEdit.professional_college || '',
        college_number: userToEdit.collegeNumber || userToEdit.college_number || ''
      });
    } else {
      setEditingUser(null);
      setUserFormData({
        name: '',
        lastname: '',
        username: '',
        cedula: '',
        email: '',
        password: '',
        role: 'medico',
        prefix: '',
        specialty: '',
        mpps_number: '',
        professional_college: '',
        college_number: ''
      });
    }
    setIsUserDialogOpen(true);
  };

  const handleCloseUserDialog = () => {
    setIsUserDialogOpen(false);
    setEditingUser(null);
    setUserFormData({
      name: '',
      lastname: '',
      username: '',
      cedula: '',
      email: '',
      password: '',
      role: 'medico',
      prefix: '',
      specialty: '',
      mpps_number: '',
      professional_college: '',
      college_number: ''
    });
  };

  const handleSaveUser = async () => {
    if (!userFormData.name || !userFormData.email || !userFormData.username) {
      setMessage({ type: 'error', text: 'Nombre, email y usuario son obligatorios' });
      return;
    }
    
    if (!editingUser && !userFormData.password) {
      setMessage({ type: 'error', text: 'La contraseña es obligatoria para nuevos usuarios' });
      return;
    }

    setIsLoading(true);
    try {
      const payload: any = {
        name: userFormData.name,
        lastname: userFormData.lastname,
        username: userFormData.username,
        cedula: userFormData.cedula,
        email: userFormData.email,
        role: userFormData.role,
        prefix: userFormData.prefix,
        specialty: userFormData.specialty,
        mppsNumber: userFormData.mpps_number,
        professionalCollege: userFormData.professional_college,
        collegeNumber: userFormData.college_number
      };
      
      if (userFormData.password) {
        payload.password = userFormData.password;
      }

      const url = editingUser 
        ? `${API_URL}/api/users/${editingUser.id}`
        : `${API_URL}/api/users`;
      
      const method = editingUser ? 'PUT' : 'POST';
      
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setMessage({ 
          type: 'success', 
          text: editingUser ? 'Usuario actualizado' : 'Usuario creado' 
        });
        handleCloseUserDialog();
        fetchUsers();
        setTimeout(() => setMessage(null), 3000);
      } else {
        const errorData = await response.json();
        let errorMsg = 'Error al guardar usuario';
        if (errorData.message) {
          errorMsg = errorData.message;
        } else if (errorData.error) {
            errorMsg = errorData.error;
        }
        throw new Error(errorMsg);
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('¿Eliminar este usuario? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API_URL}/api/users/${userId}`, {
        method: 'DELETE',
        headers
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Usuario eliminado' });
        fetchUsers();
        setTimeout(() => setMessage(null), 3000);
      } else {
        throw new Error('Error al eliminar usuario');
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message });
    }
  };

const [servicePrices, setServicePrices] = useState<any[]>([]);
const [openPriceDialog, setOpenPriceDialog] = useState(false);
const [currentPrice, setCurrentPrice] = useState({ id: '', name: '', category: 'CONSULTA', amount: '' });

// Cargar precios al entrar
const fetchPrices = async () => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/api/finance/prices`, {
        headers: { 'Authorization': `Bearer ${token}` }
    });
    if (res.ok) setServicePrices(await res.json());
};

useEffect(() => {
    fetchPrices();
}, []);

const handleSavePrice = async () => {
    if (!currentPrice.name || !currentPrice.amount) return alert("Nombre y Monto obligatorios");
    
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/api/finance/prices`, {
        method: 'POST',
        headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(currentPrice)
    });

    if (res.ok) {
        setOpenPriceDialog(false);
        setCurrentPrice({ id: '', name: '', category: 'CONSULTA', amount: '' });
        fetchPrices();
    }
};

const handleDeletePrice = async (id: string) => {
    if (!window.confirm("¿Eliminar este servicio?")) return;
    const token = localStorage.getItem('token');
    await fetch(`${API_URL}/api/finance/prices/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
    });
    fetchPrices();
};

  // ===== MANEJO DE LOGOS (ADMIN) =====
  const handleLogoCartaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setLogoCartaFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewCarta(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoRecipeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setLogoRecipeFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewRecipe(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveConfig = async () => {
    try {
      const formData = new FormData();
      
      formData.append('doctorName', config.doctorName || '');
      formData.append('companyInfo', config.companyInfo || '');
      formData.append('footerAddress', config.footerAddress || '');
      formData.append('font', config.font || 'Arial');
      formData.append('fontSize', String(config.fontSize || 10));
      formData.append('favoriteSpecialties', JSON.stringify(selectedFavs));
	  
      if (logoCartaFile) {
        formData.append('logoCarta', logoCartaFile);
      }
      if (logoRecipeFile) {
        formData.append('logoRecipe', logoRecipeFile);
      }
      
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${API_URL}/api/config`, { 
        method: 'PUT', 
        headers,
        body: formData 
      });
      
      if (res.ok) {
        const result = await res.json();
        
        if (result.config) {
          setConfig(result.config);
          
          if (result.config.logoCartaUrl) {
            setPreviewCarta(processImageUrl(result.config.logoCartaUrl));
          }
          if (result.config.logoRecipeUrl) {
            setPreviewRecipe(processImageUrl(result.config.logoRecipeUrl));
          }
        }
        
        setMessage({ 
          type: 'success', 
          text: '✅ Configuración guardada correctamente. Los cambios se han aplicado.' 
        });
        
        setLogoCartaFile(null);
        setLogoRecipeFile(null);
        
        setTimeout(() => setMessage(null), 4000);
      } else {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Error al guardar');
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: 'Error al guardar configuración: ' + (error as Error).message 
      });
    }
  };

  // ===== MANEJO DE FIRMA DIGITAL (USUARIO) =====
  const handleSignatureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
        setMessage({ type: 'error', text: 'Solo se permiten imágenes PNG o JPG' });
        return;
      }
      
      if (file.size > 2 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'La imagen debe ser menor a 2MB' });
        return;
      }
      
      setSignatureFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewSignature(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveSignature = async () => {
    setIsLoading(true);
    setMessage(null);
    
    try {
      let signatureUrlToSave = userConfig.signature_url;
      
      if (signatureFile) {
        const formData = new FormData();
        formData.append('signature', signatureFile);
        formData.append('userId', user?.id || '');
        
        const token = localStorage.getItem('token');
        const uploadHeaders: Record<string, string> = {};
        if (token) uploadHeaders['Authorization'] = `Bearer ${token}`;

        const uploadRes = await fetch(`${API_URL}/api/upload-signature`, {
          method: 'POST',
          headers: uploadHeaders,
          body: formData,
        });
        
        if (!uploadRes.ok) {
          throw new Error('Error al subir la firma');
        }
        
        const uploadData = await uploadRes.json();
        signatureUrlToSave = uploadData.signatureUrl || uploadData.signature_url;
      }
      
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API_URL}/api/users/${user?.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          signatureUrl: signatureUrlToSave,
          prefix: userConfig.prefix,
          specialty: userConfig.specialty,
          mppsNumber: userConfig.mpps_number,
          professionalCollege: userConfig.professional_college,
          collegeNumber: userConfig.college_number
        }),
      });
      
      if (!response.ok) {
        throw new Error('Error al guardar datos');
      }
      
      setMessage({ type: 'success', text: 'Firma y datos profesionales guardados correctamente' });
      setSignatureFile(null);
      
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Error al guardar: ' + (error as Error).message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSignature = async () => {
    if (!window.confirm('¿Eliminar firma digital?')) return;
    
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API_URL}/api/users/${user?.id}/signature`, {
        method: 'DELETE',
        headers
      });
      
      if (response.ok) {
        setUserConfig({ ...userConfig, signature_url: '' });
        setPreviewSignature('');
        setSignatureFile(null);
        setMessage({ type: 'success', text: 'Firma eliminada' });
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error al eliminar firma' });
    } finally {
      setIsLoading(false);
    }
  };

  // ===== CAMBIO DE CONTRASEÑA =====
  const handleChangePassword = async () => {
    if (!passData.oldPassword || !passData.newPassword) {
      return setMessage({type:'error', text:'Complete todos los campos.'});
    }
    if (passData.newPassword !== passData.confirmPassword) {
      return setMessage({type:'error', text:'Las contraseñas no coinciden.'});
    }

    try {
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${API_URL}/api/change-password`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ 
          userId: user?.id, 
          oldPassword: passData.oldPassword, 
          newPassword: passData.newPassword 
        })
      });
      
      const data = await res.json();
      
      if (data.success) {
        setMessage({ type: 'success', text: 'Contraseña actualizada.' });
        setPassData({ oldPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        setMessage({ type: 'error', text: data.message || 'Error al cambiar contraseña' });
      }
    } catch (e) { 
      setMessage({ type: 'error', text: 'Error de conexión.' }); 
    }
  };

  // Calcular índice de tabs dinámicamente
 const getTabIndex = () => {
    if (isAdmin) {
      return { logos: 0, usuarios: 1, recursos: 2, precios: 3, firma: 4, seguridad: 5};
    } else {
      return { logos: -1, usuarios: -1, recursos: -1, precios: -1, firma: 0, seguridad: 1};
    }
  };
  
  const tabIndex = getTabIndex();

  const getUserFullName = () => {
    if (!user) return '';
    // @ts-ignore
    return `${user.name || ''} ${user.lastname || ''}`.trim();
  };

  return (
    <Box sx={{ p: 3 }}>
        <style>{`
  @keyframes slideIn {
    from {
      transform: translateX(400px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`}</style>
      <Typography variant="h4" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>
        ⚙️ Configuración {isAdmin ? 'del Sistema' : 'Personal'}
      </Typography>
      
      {message && (
        <Alert 
            severity={message.type} 
            onClose={() => setMessage(null)}
            sx={{ 
            position: 'fixed',
            bottom: 24,
            right: 24,
            minWidth: 400,
            zIndex: 9999,
            boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
            animation: 'slideIn 0.3s ease-out'
            }}
        >
            {message.text}
        </Alert>
        )}

      <Paper sx={{ width: '100%', mt: 2 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          indicatorColor="primary" 
          textColor="primary"
          variant="fullWidth"
        >
          {isAdmin && <Tab icon={<ImageIcon/>} label="Logos y Membrete" />}
          {isAdmin && <Tab icon={<PeopleIcon/>} label="Usuarios" />}
          {isAdmin && <Tab icon={<LocationOnIcon/>} label="Recursos" />}
		  {isAdmin && <Tab icon={<AddIcon/>} label="Precios y Servicios" />} 
          <Tab icon={<DrawIcon/>} label="Mi Firma Digital" />
          <Tab icon={<LockIcon/>} label="Seguridad" />
        </Tabs>

        {/* ============= TAB: LOGOS Y MEMBRETE (SOLO ADMIN) ============= */}
        {isAdmin && (
          <TabPanel value={tabValue} index={0}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <strong>💡 Recomendación:</strong> Diseña tus logos en Canva o Photoshop con las dimensiones indicadas para mejores resultados.
            </Alert>

            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Card sx={{ border: '2px solid #1976d2', borderRadius: 2, height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                      📄 Logo para Informes (Carta)
                    </Typography>
                    
                    <Alert severity="info" icon={false} sx={{ mb: 2, fontSize: '0.85rem' }}>
                      <strong>Dimensiones:</strong> 2100×300 px (horizontal)<br/>
                      <strong>Formato:</strong> PNG (recomendado) o JPG<br/>
                      <strong>Uso:</strong> Informes médicos tamaño carta
                    </Alert>

                    <Box sx={{ border: '2px dashed #1976d2', borderRadius: 2, p: 2, textAlign: 'center', minHeight: 150, bgcolor: '#f5f7fa', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                      {previewCarta ? (
                        <img src={previewCarta} alt="Logo Carta" style={{ maxWidth: '100%', maxHeight: 120, objectFit: 'contain' }} />
                      ) : (
                        <Typography variant="body2" color="text.secondary">Sin logo configurado</Typography>
                      )}
                    </Box>

                    <input type="file" ref={logoCartaInputRef} hidden accept="image/png,image/jpeg,image/jpg" onChange={handleLogoCartaChange} />
                    <Button fullWidth variant="contained" startIcon={<UploadFileIcon />} onClick={() => logoCartaInputRef.current?.click()}>
                      {logoCartaFile ? 'Cambiar Imagen' : 'Seleccionar Logo'}
                    </Button>
                    {logoCartaFile && (
                      <Typography variant="caption" display="block" sx={{ mt: 1, color: 'success.main' }}>
                        ✓ Archivo seleccionado: {logoCartaFile.name}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ border: '2px solid #2e7d32', borderRadius: 2, height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
                      💊 Logo para Recipes (Media Carta)
                    </Typography>
                    
                    <Alert severity="success" icon={false} sx={{ mb: 2, fontSize: '0.85rem' }}>
                      <strong>Dimensiones:</strong> 1000×250 px (compacto)<br/>
                      <strong>Formato:</strong> PNG (recomendado) o JPG<br/>
                      <strong>Uso:</strong> Recetas, estudios, laboratorios
                    </Alert>

                    <Box sx={{ border: '2px dashed #2e7d32', borderRadius: 2, p: 2, textAlign: 'center', minHeight: 150, bgcolor: '#f1f8f4', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                      {previewRecipe ? (
                        <img src={previewRecipe} alt="Logo Recipe" style={{ maxWidth: '100%', maxHeight: 100, objectFit: 'contain' }} />
                      ) : (
                        <Typography variant="body2" color="text.secondary">Sin logo configurado</Typography>
                      )}
                    </Box>

                    <input type="file" ref={logoRecipeInputRef} hidden accept="image/png,image/jpeg,image/jpg" onChange={handleLogoRecipeChange} />
                    <Button fullWidth variant="contained" color="success" startIcon={<UploadFileIcon />} onClick={() => logoRecipeInputRef.current?.click()}>
                      {logoRecipeFile ? 'Cambiar Imagen' : 'Seleccionar Logo'}
                    </Button>
                    {logoRecipeFile && (
                      <Typography variant="caption" display="block" sx={{ mt: 1, color: 'success.main' }}>
                        ✓ Archivo seleccionado: {logoRecipeFile.name}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  📍 Pie de Página (Compartido)
                </Typography>
                
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Dirección, Teléfonos, Email, Redes Sociales"
                  placeholder={`<Escriba la dirección aquí...>\n<Si requiere otra línea presione Enter y escriba aquí...>`}
                  value={config.footerAddress || ''}
                  onChange={(e) => {
                    if (e.target.value.length <= 280) {
                        setConfig({...config, footerAddress: e.target.value});
                    }
                  }}
                  helperText={
                    <Box component="span" sx={{ display: 'block', mt: 1 }}>
                      <Typography variant="caption" display="block" color={(config.footerAddress?.length || 0) > 140 ? "error" : "textSecondary"}>
                        • Caracteres: <strong>{config.footerAddress?.length || 0} / 140</strong> recomendados por línea.
                      </Typography>
                      <Typography variant="caption" display="block" color="textSecondary">
                        • Use <strong>Enter</strong> para bajar a la siguiente línea si es necesario.
                      </Typography>
                    </Box>
                  }
                  error={(config.footerAddress?.length || 0) > 140} 
                />
              </Grid>
            </Grid>
			
			{/* Buscador de Especialidades Favoritas */}
			<Grid item xs={12} sx={{ mt: 3 }}>
				<Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					⭐ Mis Especialidades Frecuentes
				</Typography>
				<Alert severity="info" sx={{ mb: 2 }}>
					Seleccione las especialidades que desea ver en sus Catálogos, Récipes y Reportes. 
					Si deja este campo vacío, se mostrarán todas por defecto.
				</Alert>
				<Autocomplete
					multiple
					options={ALL_SPECIALTIES}
					value={selectedFavs}
					onChange={(_, newValue) => setSelectedFavs(newValue)}
					renderInput={(params) => (
					<TextField {...params} label="Seleccionar especialidades..." placeholder="Ej: Urología" />
				)}
					renderTags={(value, getTagProps) =>
					value.map((option, index) => (
					<Chip label={option} {...getTagProps({ index })} color="primary" variant="filled" />
				))
			  }
			/>
			</Grid>
			

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button variant="outlined" onClick={() => { setLogoCartaFile(null); setLogoRecipeFile(null); }}>
                Cancelar
              </Button>
              <Button variant="contained" size="large" onClick={handleSaveConfig} disabled={!logoCartaFile && !logoRecipeFile && !config.footerAddress}>
                💾 Guardar Configuración
              </Button>
            </Box>
          </TabPanel>
        )}

        {/* ============= TAB: USUARIOS (SOLO ADMIN) ============= */}
        {isAdmin && (
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6">Gestión de Usuarios</Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenUserDialog()}>
                Nuevo Usuario
              </Button>
            </Box>

            {isLoadingUsers ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={3}>
                <Table>
                  <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell><strong>Nombre Completo</strong></TableCell>
                      <TableCell><strong>Usuario (Login)</strong></TableCell>
                      <TableCell><strong>Cédula</strong></TableCell>
                      <TableCell><strong>Correo</strong></TableCell>
                      <TableCell><strong>Rol</strong></TableCell>
                      <TableCell align="center"><strong>Acciones</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.id} hover>
                        <TableCell>
                          {u.prefix ? `${u.prefix} ` : ''}{`${u.name} ${u.lastname || ''}`}
                        </TableCell>
                        <TableCell>
                          <Chip label={u.username || 'N/A'} color="primary" variant="outlined" size="small" sx={{ fontWeight: 'bold' }} />
                        </TableCell>
                        <TableCell>{u.cedula || '---'}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {u.role === 'asistente' ? 'Asistente Administrativo' : 
                             u.role === 'administrador' ? 'Administrador' : 'Médico Tratante'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton color="primary" onClick={() => handleOpenUserDialog(u)} title="Editar">
                            <EditIcon />
                          </IconButton>
                          <IconButton color="error" onClick={() => handleDeleteUser(u.id)} title="Eliminar">
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>
        )}

        {/* ============= TAB: RECURSOS (SOLO ADMIN) ============= */}
        {isAdmin && (
          <TabPanel value={tabValue} index={tabIndex.recursos}>
            <Box sx={{ mt: 0 }}>
                <Divider sx={{ mb: 2 }}><Chip label="ADMINISTRACIÓN DE RECURSOS" /></Divider>
                <LocationManager />
            </Box>
          </TabPanel>
        )}

        {/* Dialog para Crear/Editar Usuario */}
        <Dialog open={isUserDialogOpen} onClose={handleCloseUserDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nombre *"
                  value={userFormData.name}
                  onChange={(e) => setUserFormData({...userFormData, name: e.target.value})}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Apellido *"
                  value={userFormData.lastname}
                  onChange={(e) => setUserFormData({...userFormData, lastname: e.target.value})}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Usuario (Login) *"
                  value={userFormData.username}
                  onChange={(e) => setUserFormData({...userFormData, username: e.target.value})}
                  helperText="Para iniciar sesión"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Cédula"
                  value={userFormData.cedula}
                  onChange={(e) => {
                    let value = e.target.value.toUpperCase().trim();
                    if (value.match(/^[VEve]\d{6,9}$/)) {
                      value = value.charAt(0) + '-' + value.slice(1);
                    }
                    setUserFormData({...userFormData, cedula: value});
                  }}
                  placeholder="V-7227147 o E-1234567"
                  helperText="Venezolanos: V-xxxxxxx | Extranjeros: E-xxxxxxx"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Correo Electrónico *"
                  type="email"
                  value={userFormData.email}
                  onChange={(e) => setUserFormData({...userFormData, email: e.target.value})}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={editingUser ? "Contraseña (dejar vacío para no cambiar)" : "Contraseña *"}
                  type={showPassword ? "text" : "password"}
                  value={userFormData.password}
                  onChange={(e) => setUserFormData({...userFormData, password: e.target.value})}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  helperText={!editingUser && "Visible para evitar errores de tipeo"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Rol *</InputLabel>
                  <Select
                    value={userFormData.role}
                    onChange={(e) => setUserFormData({...userFormData, role: e.target.value})}
                    label="Rol *"
                  >
                    <MenuItem value="medico">Médico Tratante</MenuItem>
                    <MenuItem value="administrador">Administrador</MenuItem>
                    <MenuItem value="asistente">Asistente Administrativo</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }}>
                  <Chip label="Datos Profesionales (Opcional)" />
                </Divider>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Prefijo</InputLabel>
                  <Select
                    value={userFormData.prefix || ''}
                    onChange={(e) => setUserFormData({...userFormData, prefix: e.target.value})}
                    label="Prefijo"
                  >
                    <MenuItem value="">Sin prefijo</MenuItem>
                    <MenuItem value="Dr.">Dr. (Doctor)</MenuItem>
                    <MenuItem value="Dra.">Dra. (Doctora)</MenuItem>
                    <MenuItem value="Lic.">Lic. (Licenciado/a)</MenuItem>
                    <MenuItem value="TSU">TSU (Técnico Superior)</MenuItem>
                    <MenuItem value="T.E.">T.E. (Técnico en Enfermería)</MenuItem>
                    <MenuItem value="Aux.">Aux. (Auxiliar)</MenuItem>
                    <MenuItem value="Esp.">Esp. (Especialista)</MenuItem>
                    <MenuItem value="MSc">MSc (Magíster)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="Especialidad"
                  value={userFormData.specialty}
                  onChange={(e) => setUserFormData({...userFormData, specialty: e.target.value})}
                  placeholder="Ej: Urología - Ginecobstetricia"
                  helperText="Área de especialización profesional"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Número MPPS"
                  value={userFormData.mpps_number}
                  onChange={(e) => setUserFormData({...userFormData, mpps_number: e.target.value})}
                  placeholder="Ej: 100301"
                  helperText="Solo para médicos (Venezuela)"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Colegio"
                  value={userFormData.professional_college}
                  onChange={(e) => setUserFormData({...userFormData, professional_college: e.target.value.toUpperCase()})}
                  placeholder="CMA, CEA, CPA"
                  helperText="Ej: CMA, CEA, COP"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Número"
                  value={userFormData.college_number}
                  onChange={(e) => setUserFormData({...userFormData, college_number: e.target.value})}
                  placeholder="10366"
                  helperText="N° de colegiatura"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseUserDialog}>Cancelar</Button>
            <Button variant="contained" onClick={handleSaveUser} disabled={isLoading}>
              {isLoading ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ============= TAB: FIRMA DIGITAL (TODOS) ============= */}
        <TabPanel value={tabValue} index={tabIndex.firma}>
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h5" gutterBottom align="center" color="primary">
              ✍️ Mi Firma Digital
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              <strong>💡 Para qué sirve:</strong> Tu firma aparecerá automáticamente al final de todos los informes que generes, 
              junto con tus datos profesionales (especialidad, MPPS, CMA).
            </Alert>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card sx={{ border: '2px solid #9c27b0' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ color: '#9c27b0' }}>
                      📝 Imagen de Firma
                    </Typography>
                    
                    <Box sx={{ border: '2px dashed #9c27b0', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#faf5ff', minHeight: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', mb: 2 }}>
                      {previewSignature ? (
                        <>
                          <img src={previewSignature} alt="Firma" style={{ maxWidth: '300px', maxHeight: '150px', objectFit: 'contain' }} />
                          <IconButton size="small" color="error" onClick={handleDeleteSignature} sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'white', '&:hover': { bgcolor: '#ffebee' } }}>
                            <DeleteIcon />
                          </IconButton>
                        </>
                      ) : (
                        <Box sx={{ textAlign: 'center' }}>
                          <DrawIcon sx={{ fontSize: 60, color: '#9c27b0', opacity: 0.3, mb: 1 }} />
                          <Typography variant="body2" color="text.secondary">Sin firma configurada</Typography>
                        </Box>
                      )}
                    </Box>

                    <input type="file" ref={signatureInputRef} hidden accept="image/png,image/jpeg,image/jpg" onChange={handleSignatureChange} />
                    <Button fullWidth variant="contained" color="secondary" startIcon={<UploadFileIcon />} onClick={() => signatureInputRef.current?.click()} sx={{ mb: 1 }}>
                      {previewSignature ? 'Cambiar Firma' : 'Subir Firma'}
                    </Button>
                    {signatureFile && (
                      <Typography variant="caption" display="block" sx={{ color: 'success.main' }}>
                        ✓ Nuevo archivo: {signatureFile.name}
                      </Typography>
                    )}

                    <Alert severity="warning" sx={{ mt: 2 }} icon={false}>
                      <strong>📏 Recomendaciones:</strong>
                      <br/>• Fondo transparente (PNG) o blanco
                      <br/>• Tamaño: 600×200 píxeles aprox.
                      <br/>• Firma clara y legible
                      <br/>• Máximo 2MB
                    </Alert>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>🏥 Datos Profesionales</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Estos datos aparecerán junto a tu firma en los informes
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                          <InputLabel>Prefijo</InputLabel>
                          <Select
                            value={userConfig.prefix || ''}
                            onChange={(e) => setUserConfig({...userConfig, prefix: e.target.value})}
                            label="Prefijo"
                          >
                            <MenuItem value="">Sin prefijo</MenuItem>
                            <MenuItem value="Dr.">Dr. (Doctor)</MenuItem>
                            <MenuItem value="Dra.">Dra. (Doctora)</MenuItem>
                            <MenuItem value="Lic.">Lic. (Licenciado/a)</MenuItem>
                            <MenuItem value="TSU">TSU (Técnico Superior)</MenuItem>
                            <MenuItem value="T.E.">T.E. (Técnico en Enfermería)</MenuItem>
                            <MenuItem value="Aux.">Aux. (Auxiliar)</MenuItem>
                            <MenuItem value="Esp.">Esp. (Especialista)</MenuItem>
                            <MenuItem value="MSc">MSc (Magíster)</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={8}>
                        <TextField fullWidth label="Especialidad" value={userConfig.specialty} onChange={(e) => setUserConfig({...userConfig, specialty: e.target.value})} placeholder="Ej: Urología - Ginecobstetricia" helperText="Tu especialidad médica o profesional" />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Número MPPS" value={userConfig.mpps_number} onChange={(e) => setUserConfig({...userConfig, mpps_number: e.target.value})} placeholder="Ej: 100301" helperText="Solo médicos (Venezuela)" />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField fullWidth label="Colegio" value={userConfig.professional_college} onChange={(e) => setUserConfig({...userConfig, professional_college: e.target.value.toUpperCase()})} placeholder="CMA, CEA" helperText="Ej: CMA, CEA, COP" />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField fullWidth label="Número" value={userConfig.college_number} onChange={(e) => setUserConfig({...userConfig, college_number: e.target.value})} placeholder="10366" helperText="N° de colegiatura" />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card sx={{ bgcolor: '#f5f5f5' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>👁️ Vista Previa</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Así se verá tu firma en los informes:</Typography>

                    <Box sx={{ bgcolor: 'white', p: 3, borderRadius: 1, border: '1px solid #ddd', textAlign: 'center' }}>
                      {previewSignature && (
                        <img src={previewSignature} alt="Firma preview" style={{ maxWidth: '250px', maxHeight: '100px', marginBottom: '10px' }} />
                      )}
                      <Typography variant="body1" sx={{ fontWeight: 'bold', fontFamily: 'Arial', fontSize: '9pt' }}>
                        {userConfig.prefix ? `${userConfig.prefix} ` : ''}{getUserFullName()}
                      </Typography>
                      {userConfig.specialty && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Arial', fontStyle: 'italic', fontSize: '8pt' }}>
                          {userConfig.specialty}
                        </Typography>
                      )}
                      {(userConfig.cedula || userConfig.mpps_number || (userConfig.professional_college && userConfig.college_number)) && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Arial', fontSize: '8pt' }}>
                          {userConfig.cedula && `C.I.: ${userConfig.cedula}`}
                          {userConfig.cedula && userConfig.mpps_number && ' | '}
                          {userConfig.mpps_number && `MPPS: ${userConfig.mpps_number}`}
                          {(userConfig.cedula || userConfig.mpps_number) && (userConfig.professional_college && userConfig.college_number) && ' | '}
                          {(userConfig.professional_college && userConfig.college_number) && `${userConfig.professional_college}: ${userConfig.college_number}`}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Button variant="contained" color="primary" size="large" fullWidth onClick={handleSaveSignature} disabled={isLoading} sx={{ py: 1.5 }}>
                  {isLoading ? '💾 Guardando...' : '💾 Guardar Firma y Datos'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>
		
		{/* ============= SERVICIOS Y PRECIOS (SOLO ADMIN) ============= */}
{isAdmin && (
  <TabPanel value={tabValue} index={tabIndex.precios}>
    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Lista de Precios Oficiales</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenPriceDialog(true)}>
            Nuevo Servicio/Precio
        </Button>
    </Box>

    <Grid container spacing={2}>
        {servicePrices.map((item) => (
            <Grid item xs={12} md={4} key={item.id}>
                <Paper variant="outlined" sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderLeft: item.category === 'CONSULTA' ? '5px solid #1976d2' : '5px solid #9c27b0' }}>
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold">{item.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.category}</Typography>
                        <Typography variant="h6" color="primary">${item.amount}</Typography>
                    </Box>
                    <Box>
                        <IconButton size="small" onClick={() => { setCurrentPrice(item); setOpenPriceDialog(true); }}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeletePrice(item.id)}><DeleteIcon fontSize="small" /></IconButton>
                    </Box>
                </Paper>
            </Grid>
        ))}
    </Grid>

    {/* Diálogo para Agregar/Editar Precio */}
    <Dialog open={openPriceDialog} onClose={() => setOpenPriceDialog(false)}>
        <DialogTitle>{currentPrice.id ? 'Editar Precio' : 'Nuevo Precio'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField label="Nombre del Servicio" fullWidth value={currentPrice.name} onChange={(e) => setCurrentPrice({...currentPrice, name: e.target.value})} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField select label="Categoría" fullWidth value={currentPrice.category} onChange={(e) => setCurrentPrice({...currentPrice, category: e.target.value})}>
                        <MenuItem value="CONSULTA">Consulta Médica</MenuItem>
                        <MenuItem value="PROCEDIMIENTO">Procedimiento</MenuItem>
                    </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField label="Monto ($)" type="number" fullWidth value={currentPrice.amount} onChange={(e) => setCurrentPrice({...currentPrice, amount: e.target.value})} />
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setOpenPriceDialog(false)}>Cancelar</Button>
            <Button variant="contained" onClick={handleSavePrice}>Guardar</Button>
        </DialogActions>
    </Dialog>
</TabPanel>
)}

        {/* ============= TAB: SEGURIDAD (TODOS) ============= */}
        <TabPanel value={tabValue} index={tabIndex.seguridad}>
          <Box sx={{ maxWidth: 400, mx: 'auto', py: 2 }}>
            <Typography variant="h6" gutterBottom align="center">
              🔒 Cambiar Contraseña
            </Typography>
            
            <TextField fullWidth type="password" label="Contraseña Actual" margin="normal" value={passData.oldPassword} onChange={e => setPassData({...passData, oldPassword: e.target.value})} />
            <TextField fullWidth type="password" label="Nueva Contraseña" margin="normal" value={passData.newPassword} onChange={e => setPassData({...passData, newPassword: e.target.value})} />
            <TextField fullWidth type="password" label="Confirmar Nueva Contraseña" margin="normal" value={passData.confirmPassword} onChange={e => setPassData({...passData, confirmPassword: e.target.value})} />
            
            <Button variant="contained" color="secondary" fullWidth sx={{ mt: 2 }} onClick={handleChangePassword}>
              Actualizar Contraseña
            </Button>
          </Box>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default ConfigurationPage;